import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, IconButton, keyframes, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { SignedSlider } from "../../../components/UIToolkit/Inputs/SignedSlider";
import { FONT_SIZE, ICON_SIZE, MOTION } from "../../../theme/tokens";
import type { PbjModelUpsertBody, PbjPrediction } from "../../../utils/models";
import { PBJ_ALPHA } from "./colors";
import { effectiveBaselineForPrefix } from "./cumulativeScoring.utils";
import { levelAbove, levelBelow, pointLevelSpends } from "./pointLevels.utils";
import type { PeriodMode } from "./RecommendationsSidebar";
import { calculateRecData, displaySpendForLabel, MAX_HPPD_PER_RECOMMENDATION } from "./recMath.utils";
import { CUT_SIDE_MIN_SHARE, cutFloorState, OVERRIDE_KEY_BY_WAGE_KEY, SPEND_STEP, wageTooltipKey } from "./recommendationRow.utils";
import type { PlanItem, RecommendationType } from "./recommendations.constants";

// One-shot land-pulse for a slider the Suggest action just repositioned: a ring grows out of the thumb
// and fades (ENTRANCE_MD, ease-out) so the eye catches the move. Drawn on the thumb's own `::after` (MUI's
// invisible 42px hit area — only its border and opacity change, never its size) and animated on
// transform + opacity only, so it stays on the compositor. Applied only while the lever is suggested,
// so it never fires on untouched or user-dragged sliders; reduced motion leaves the ring hidden. The
// color is injected at render time (theme-derived) via a CSS var so the keyframe stays static.
const PULSE_RING = {
	// Scale of the 42px hit area the ring starts and ends at: from just around the thumb to ~2× it.
	FROM_SCALE: 0.3,
	TO_SCALE: 0.6,
	BORDER_PX: 3,
} as const;
// Includes the column's 8px horizontal padding on each side plus the two stepper buttons, leaving a
// 100px rail that is comfortable to grab even when the outcome copy is long.
const SLIDER_COLUMN_MIN_WIDTH_PX = 172;
// The − / + buttons sit beside the rail, not the end captions under it, so they are nudged down by
// the slider's own top padding to line up with the thumb.
const STEPPER_BUTTON_SX = { p: 0.25, mt: 0.5, color: "text.secondary", flexShrink: 0 } as const;
const SLIDER_LAND_PULSE = keyframes`
	from { opacity: 1; transform: translate(-50%, -50%) scale(${PULSE_RING.FROM_SCALE}); }
	to { opacity: 0; transform: translate(-50%, -50%) scale(${PULSE_RING.TO_SCALE}); }
`;

type Props = {
	rec: RecommendationType;
	wage: number;
	// Theme-derived categorical accent for this lever.
	color: string;
	// Live slider spend ($/applicable-PPD, signed).
	currentSpend: number;
	isSuggested: boolean;
	isInert: boolean;
	// Bumped by every Suggest / Reset: the slider is keyed on it so the land pulse replays even when the
	// suggested value didn't change. Never changes on a drag, so the thumb keeps focus mid-drag.
	suggestNonce: number;
	prediction: PbjPrediction;
	census: number;
	periodMode: PeriodMode;
	// The live config draft — read only to tell the wage tooltip override vs. computed.
	draft: PbjModelUpsertBody;
	// The current Action Plan and whether THIS lever is in it — together they give the running
	// HPPD the lever's point levels (the slider's ticks) are measured from.
	selectedPlan: PlanItem[];
	isSelected: boolean;
	onChange: (newSpend: number) => void;
	// Opens the wage-overrides dialog. The assumed wage drives every figure on this control, so the
	// ⓘ that explains it is also where it gets changed — the panel no longer carries a settings cog.
	onEditWages: () => void;
};

/**
 * The lever card's control column: the "Spend PPD" / "Savings PPD" readout with its wage tooltip, the
 * two-direction slider (add right of 0, cut left of it, down to the backend's per-lever cut limit) with
 * its end captions. The note naming the cut limit lives on the row, full width (see RecommendationRow).
 */
export const RecommendationSliderControl: FC<Props> = ({
	rec,
	wage,
	color,
	currentSpend,
	isSuggested,
	isInert,
	suggestNonce,
	prediction,
	census,
	periodMode,
	draft,
	selectedPlan,
	isSelected,
	onChange,
	onEditWages,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const pulseRingC = alpha(theme.palette.primary.main, PBJ_ALPHA.BORDER);
	const savingsInk = theme.palette.success.main;
	const isCut = currentSpend < 0;

	// Slider max = the largest HPPD add (`impact = spend / wage`, so `max = wage ×
	// MAX_HPPD_PER_RECOMMENDATION`, the same cap the BE greedy enforces); slider min = the backend's
	// per-lever cut limit converted to spend (0 when the lever can't be cut). Both bounds widen to
	// `currentSpend` so a plan value can never sit outside the rail.
	const { cutFloor } = cutFloorState(rec, prediction, wage, census, currentSpend);
	const sliderMin = Math.min(-cutFloor, currentSpend);
	const sliderMax = Math.max(wage * MAX_HPPD_PER_RECOMMENDATION, currentSpend);

	// Where this lever's points actually change — the ticks on the rail and the stops the − / +
	// buttons jump between. Measured from the plan prefix before this lever, the same baseline its
	// "+X pts" is scored against, so a tick is exactly where that figure moves.
	const pointLevels = useMemo(
		() =>
			pointLevelSpends({
				rec,
				wage,
				prediction,
				baseline: effectiveBaselineForPrefix(selectedPlan, prediction.categories, isSelected ? rec.id : undefined),
				min: sliderMin,
				max: sliderMax,
			}),
		[rec, wage, prediction, selectedPlan, isSelected, sliderMin, sliderMax],
	);

	// Every dollar figure here — the readout, the end captions, the screen-reader value — is the
	// per-CALENDAR-day rate over the window the period toggle measures, so weekday and weekend levers
	// compare directly and match the Action Plan's per-item PPD. The slider's own value stays in raw
	// $/applicable-PPD units; only what's shown is normalized.
	// A cut is shown at its PRICED spend — the whole hours a day it commits (recMath `pricedSpendFor`) —
	// so the readout, the screen-reader value and the deepest-cut caption match the card's "Saves $X" and
	// the Action Plan's "$X PPD saved". An add shows its exact spend, as it always has.
	const pricedAt = (spend: number): number => (spend < 0 ? calculateRecData(rec, spend, wage, prediction, census).pricedSpend : spend);
	const displayDollars = (spend: number): string => displaySpendForLabel(rec, Math.abs(pricedAt(spend)), prediction, periodMode).toFixed(2);
	const ariaValueText = (value: number): string =>
		t(value < 0 ? "pbj_model.recommendations.slider_value_savings_aria" : "pbj_model.recommendations.slider_value_spend_aria", {
			value: displayDollars(value),
		});
	const endLabels = {
		min:
			sliderMin < 0
				? t("pbj_model.recommendations.slider_min_label", { value: displayDollars(sliderMin) })
				: t("pbj_model.recommendations.spend_format", { value: displayDollars(0) }),
		max: t("pbj_model.recommendations.slider_max_label", { value: displayDollars(sliderMax) }),
	};

	return (
		<Box sx={{ px: 1, flex: 1, minWidth: { xs: 0, sm: SLIDER_COLUMN_MIN_WIDTH_PX } }}>
			<Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 0 }}>
				<Stack direction="row" sx={{ alignItems: "center" }}>
					<Typography variant="caption" sx={{ color: "text.secondary" }} data-testid={`pbj-model-recommendation-spend-label-${rec.id}`}>
						{t(isCut ? "pbj_model.recommendations.savings_ppd" : "pbj_model.recommendations.spend_ppd")}
					</Typography>
					{/* The ⓘ both explains the assumed wage and edits it — clicking opens the wage-overrides
					    dialog. That replaced the settings cog in the panel header, which sat far from the
					    figures the wage drives. */}
					<InHouseTooltip
						arrow
						placement="top"
						title={`${t(wageTooltipKey(rec, draft[OVERRIDE_KEY_BY_WAGE_KEY[rec.wageKey]] != null), { value: wage.toFixed(2) })} ${t(
							"pbj_model.recommendations.wage_tooltip.edit_hint",
						)}`}
					>
						<IconButton
							size="small"
							onClick={onEditWages}
							aria-label={t("pbj_model.recommendations.wage_overrides.tooltip")}
							sx={{ p: 0.25, ml: 0.25, color: "text.secondary" }}
							data-testid={`pbj-model-recommendation-wage-info-${rec.id}`}
						>
							<InfoOutlinedIcon sx={{ fontSize: FONT_SIZE.CAPTION }} />
						</IconButton>
					</InHouseTooltip>
				</Stack>
				<InHouseTooltip
					arrow
					placement="top"
					title={
						rec.isWeekend
							? t("pbj_model.action_plan.weekend_spend_tooltip", {
									value: Math.abs(pricedAt(currentSpend)).toFixed(2),
									weekendDays: prediction.quarterWeekendDays,
									quarterDays: prediction.quarterDays,
								})
							: ""
					}
				>
					<Typography
						variant="caption"
						sx={{
							fontWeight: "bold",
							color: isCut ? savingsInk : color,
							cursor: rec.isWeekend ? "help" : "default",
							fontVariantNumeric: "tabular-nums",
							transition: `color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
							"@media (prefers-reduced-motion: reduce)": { transition: "none" },
						}}
						data-testid={`pbj-model-recommendation-spend-value-${rec.id}`}
					>
						{t("pbj_model.recommendations.spend_format", { value: displayDollars(currentSpend) })}
					</Typography>
				</InHouseTooltip>
			</Stack>
			{/* Drag for any amount; − / + jump to the next level that earns (or gives up) points — the
			    ticks on the rail. Between two ticks a dollar buys nothing, so the buttons are how a user
			    reaches a band edge without hunting for it. */}
			<Stack direction="row" spacing={0.25} sx={{ alignItems: "flex-start" }}>
				<IconButton
					size="small"
					onClick={() => onChange(levelBelow(pointLevels, currentSpend, sliderMin))}
					disabled={currentSpend <= sliderMin + SPEND_STEP / 2}
					aria-label={t("pbj_model.recommendations.step_down_aria", { title: t(rec.titleKey) })}
					sx={STEPPER_BUTTON_SX}
					data-testid={`pbj-model-recommendation-step-down-${rec.id}`}
				>
					<RemoveIcon sx={{ fontSize: ICON_SIZE.MICRO }} />
				</IconButton>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<SignedSlider
						// Remount on each Suggest / Reset so the land pulse replays. Keyed on the nonce, never on the
						// value or the suggested flag: those change on the first drag, and a remount mid-drag would
						// drop the thumb's focus and the pointer capture.
						key={suggestNonce}
						value={currentSpend}
						min={sliderMin}
						max={sliderMax}
						step={SPEND_STEP}
						origin={0}
						minSideShare={CUT_SIDE_MIN_SHARE}
						fillColor={{ positive: color, negative: savingsInk }}
						muted={isInert}
						markValues={pointLevels}
						onChange={onChange}
						aria-label={t("pbj_model.recommendations.spend_slider_aria", { title: t(rec.titleKey) })}
						getAriaValueText={ariaValueText}
						endLabels={endLabels}
						dataTestId={`pbj-model-recommendation-slider-${rec.id}`}
						sx={{
							py: 0.5,
							"--pbj-pulse": pulseRingC,
							...(isSuggested
								? {
										"& .MuiSlider-thumb::after": {
											border: `${PULSE_RING.BORDER_PX}px solid var(--pbj-pulse)`,
											boxSizing: "border-box",
											opacity: 0,
											animation: `${SLIDER_LAND_PULSE} ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT} both`,
											"@media (prefers-reduced-motion: reduce)": { animation: "none" },
										},
									}
								: {}),
						}}
					/>
				</Box>
				<IconButton
					size="small"
					onClick={() => onChange(levelAbove(pointLevels, currentSpend, sliderMax))}
					disabled={currentSpend >= sliderMax - SPEND_STEP / 2}
					aria-label={t("pbj_model.recommendations.step_up_aria", { title: t(rec.titleKey) })}
					sx={STEPPER_BUTTON_SX}
					data-testid={`pbj-model-recommendation-step-up-${rec.id}`}
				>
					<AddIcon sx={{ fontSize: ICON_SIZE.MICRO }} />
				</IconButton>
			</Stack>
		</Box>
	);
};
